import { useState, useMemo, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ServiceCard from './components/ServiceCard';
import { fetchProductList } from '@/api/product';
import type { MhProduct } from '@/types/mh';
import { groupProductsByCategory, mhProductToCard } from '@/utils/product-category';

type SortOption = 'recommended' | 'rating' | 'price_asc' | 'price_desc';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'recommended', label: '推荐排序' },
  { value: 'rating', label: '按评分排序' },
  { value: 'price_asc', label: '价格从低到高' },
  { value: 'price_desc', label: '价格从高到低' },
];

export default function ServicesPage() {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('recommended');
  const [products, setProducts] = useState<MhProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchProductList();
      if (cancelled) {
        return;
      }
      if (res.code === 0 && Array.isArray(res.data)) {
        setProducts(res.data);
        setError('');
      } else {
        setProducts([]);
        setError(res.msg || '无法加载产品列表');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.productName.toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.tags || '').toLowerCase().includes(q) ||
          p.productCode.toLowerCase().includes(q) ||
          (p.categoryName || '').toLowerCase().includes(q),
      );
    }

    if (sort === 'rating') {
      list.sort((a, b) => Number(b.sortOrder ?? 0) - Number(a.sortOrder ?? 0));
    } else if (sort === 'price_asc') {
      list.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sort === 'price_desc') {
      list.sort((a, b) => Number(b.price) - Number(a.price));
    } else {
      list.sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));
    }

    return list;
  }, [products, search, sort]);

  const groupedSections = useMemo(() => {
    return groupProductsByCategory(filteredProducts).map((g) => ({
      key: g.key,
      title: g.title,
      sort: g.sort,
      items: g.products.map(mhProductToCard),
    }));
  }, [filteredProducts]);

  const totalCount = filteredProducts.length;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
          {/* Mobile header */}
          <div className="block lg:hidden">
            <div className="text-center mb-5">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">查询服务</h1>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-2-line text-gray-400 text-base" />
                </div>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索服务名称、描述或编码..."
                className="w-full pl-11 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-gray-400 text-base" />
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Desktop header */}
          <div className="hidden lg:flex lg:items-center lg:justify-between">
            <div className="flex-1 min-w-0 pr-10">
              <h1 className="text-3xl xl:text-4xl font-bold text-gray-900 mb-2">查询服务</h1>
              <div className="mt-4 flex items-center space-x-6 text-sm text-gray-400">
                <div className="flex items-center space-x-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-gray-400" />
                  </div>
                  <span>专业可靠</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-flashlight-line text-gray-400" />
                  </div>
                  <span>快速响应</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-400" />
                  </div>
                  <span>安全保密</span>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 w-full max-w-md xl:max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-2-line text-gray-400 text-lg" />
                  </div>
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索服务名称、描述或编码..."
                  className="w-full pl-14 pr-12 py-4 text-base border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-all duration-200"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute inset-y-0 right-0 pr-5 flex items-center cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-close-circle-line text-gray-400 text-lg" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {error && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {error}
          </div>
        )}

        {/* Toolbar */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            {loading ? (
              '加载中…'
            ) : (
              <>
                找到 <span className="font-semibold text-gray-900">{totalCount}</span> 个服务
              </>
            )}
          </p>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => { setSearch(''); }}
              title="重置搜索"
              className="px-3 py-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1.5 text-sm cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line text-sm" />
              </div>
              <span className="hidden sm:inline">重置</span>
            </button>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm bg-white text-gray-700 cursor-pointer"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Grouped grid */}
        {!loading && totalCount > 0 ? (
          <div className="space-y-6">
            {groupedSections.map((section) => (
              <section
                key={section.key}
                className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4"
              >
                <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 text-gray-500 ring-1 ring-gray-100">
                      <i className="ri-price-tag-3-line" />
                    </span>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-bold text-gray-900">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  <span className="flex-shrink-0 rounded-full bg-gray-50 px-3 py-1 text-sm text-gray-500">
                    {section.items.length} 个服务
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {section.items.map((service) => (
                    <ServiceCard key={service.slug} service={service} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-center text-gray-500 text-sm">
            正在加载产品…
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 flex items-center justify-center mb-4">
              <i className="ri-search-2-line text-5xl text-gray-300" />
            </div>
            <p className="text-gray-500 text-base mb-2">暂无可用服务</p>
            <p className="text-gray-400 text-sm mb-6">请确认后台已上架产品，或调整搜索条件</p>
            <button
              onClick={() => { setSearch(''); }}
              className="px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              清除搜索
            </button>
          </div>
        )}

        <div className="h-8" />
      </div>
      </div>
      <Footer />
    </div>
  );
}
